import React, { useState, useMemo } from 'react';
import { X, Loader, Save, Search } from 'lucide-react';
import { useGetMatieresByFiltersQuery } from '../../services/matieresApi';
import apiClient from '../../config/api.config';

interface Props {
  etudiantId: number;
  matricule: string;
  nom: string;
  niveau: string;
  semestre: number;
  anneeAcademique: string;
  option?: string;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

type TypeNote = 'examen' | 'devoir1' | 'devoir2';

const ModalSaisieNote: React.FC<Props> = ({
  etudiantId, matricule, nom, niveau, semestre, anneeAcademique, option,
  onClose, onSuccess,
}) => {
  const [matiereId, setMatiereId] = useState<number | null>(null);
  const [matSearch, setMatSearch] = useState('');
  const [typeNote, setTypeNote] = useState<TypeNote>('examen');
  const [valeur, setValeur] = useState<string>('');
  const [absent, setAbsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: matieres = [] } = useGetMatieresByFiltersQuery({
    niveau, option, semestre,
  } as any);

  const matieresFiltrees = useMemo(() => {
    if (matSearch.length < 2) return (matieres as any[]).slice(0, 20);
    const q = matSearch.toLowerCase();
    return (matieres as any[])
      .filter((m: any) => m.nom_matiere?.toLowerCase().includes(q) || m.code_matiere?.toLowerCase().includes(q))
      .slice(0, 20);
  }, [matieres, matSearch]);

  const handleSave = async () => {
    if (!matiereId) {
      setErr('Sélectionnez une matière');
      return;
    }
    if (!absent && (!valeur || isNaN(Number(valeur)))) {
      setErr('Valeur invalide');
      return;
    }
    const val = absent ? 0 : Number(valeur);
    if (!absent && (val < 0 || val > 20)) {
      setErr('Note doit être entre 0 et 20');
      return;
    }

    setLoading(true);
    setErr(null);
    try {
      const semestreStr = `S${semestre}`;
      if (typeNote === 'examen') {
        await apiClient.post('/notes-examens/saisie_individuelle/', {
          etudiant: etudiantId,
          matiere: matiereId,
          note_examen: val,
          absent,
          annee_academique: anneeAcademique,
          semestre: semestreStr,
          est_rattrapage: false,
        });
      } else {
        const champ = typeNote === 'devoir1' ? { note1: val, absent1: absent } : { note2: val, absent2: absent };
        await apiClient.post('/notes-devoirs/saisie_individuelle/', {
          etudiant: etudiantId,
          matiere: matiereId,
          ...champ,
          annee_academique: anneeAcademique,
          semestre: semestreStr,
        });
      }
      onSuccess(`Note ${absent ? '(absent)' : val} enregistrée pour ${nom}`);
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Saisir une note</h3>
            <div className="text-xs text-gray-500 mt-0.5">{matricule} · {nom} · {niveau} · S{semestre}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {err && (
            <div className="p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded text-xs">
              {err}
            </div>
          )}

          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1 mb-1">
              <Search size={12} /> Matière
            </label>
            <input
              value={matSearch}
              onChange={e => { setMatSearch(e.target.value); setMatiereId(null); }}
              placeholder="Rechercher par nom ou code..."
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            />
            {!matiereId && (
              <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded max-h-40 overflow-y-auto">
                {matieresFiltrees.map((m: any) => (
                  <button key={m.id} type="button"
                    onClick={() => { setMatiereId(m.id); setMatSearch(`${m.code_matiere} · ${m.nom_matiere}`); }}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
                    <span className="font-mono text-gray-500">{m.code_matiere}</span> · {m.nom_matiere}
                  </button>
                ))}
                {matieresFiltrees.length === 0 && (
                  <div className="p-2 text-xs text-gray-400">Aucune matière</div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600 dark:text-gray-400">Type de note</label>
              <select value={typeNote} onChange={e => setTypeNote(e.target.value as TypeNote)}
                className="w-full mt-0.5 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
                <option value="examen">Examen</option>
                <option value="devoir1">Devoir 1 (D1)</option>
                <option value="devoir2">Devoir 2 (D2)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600 dark:text-gray-400">Note (0-20)</label>
              <input type="number" step="0.25" min="0" max="20"
                value={valeur} disabled={absent}
                onChange={e => setValeur(e.target.value)}
                className="w-full mt-0.5 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 disabled:opacity-50"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={absent} onChange={e => setAbsent(e.target.checked)} />
            Étudiant absent (note = 0 automatiquement)
          </label>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200">
            Annuler
          </button>
          <button onClick={handleSave} disabled={loading || !matiereId}
            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded flex items-center gap-1">
            {loading ? <Loader size={12} className="animate-spin" /> : <Save size={12} />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalSaisieNote;
